import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSelector } from 'react-redux';

import moment from 'moment-timezone';

import styles from './styles/styles.module.css';

import {
  createDelivery,
  deleteDeliveryImage,
  getDeliveryById,
  getDeliveryConfig,
  getDeliveries,
  submitDelivery,
  updateDeliveryDraft,
  uploadDeliveryImage,
  type CreateDeliveryPayload,
  type Delivery,
  type DeliveryClassification,
  type DeliveryCompany,
  type DeliveryConfig,
  type DeliveryStatus,
} from '../../api/deliveries/post';

import {
  selectDeliveryRealtimeVersion,
  selectLastDeliveryUpdate,
} from '../../Redux/slices/deliverySlice';

import {
  BsBell,
  BsBookmark,
  BsBoxSeam,
  BsCheckCircleFill,
  BsClockHistory,
  BsEnvelope,
  BsExclamationTriangle,
  BsPersonFill,
  BsSendFill,
  BsShieldFillExclamation,
  BsTrash,
  BsUpload,
} from 'react-icons/bs';

/* =========================================================
   TYPES
   ========================================================= */

type DeliveryForm = {
  classification: DeliveryClassification;

  receivedDate: string;

  receivedTime: string;

  deliveryCompany: DeliveryCompany | '';

  deliveryCompanyOther: string;

  recipientName: string;

  notes: string;

  notifyRecipientRequested: boolean;
};

type HistoryRangePreset = '1' | '7' | '30' | '90' | '180' | '365' | 'CUSTOM';

/* =========================================================
   CLASSIFICATION META
   ========================================================= */

const CLASSIFICATION_META: Record<
  DeliveryClassification,
  {
    label: string;
    description: string;
    icon: React.ReactNode;
  }
> = {
  NORMAL: {
    label: 'Normal',

    description: 'Routine delivery with no concerning indicators.',

    icon: <BsBoxSeam />,
  },

  SUSPICIOUS: {
    label: 'Suspicious',

    description: 'Delivery contains unusual or concerning indicators.',

    icon: <BsShieldFillExclamation />,
  },

  THREAT: {
    label: 'Threat',

    description: 'Delivery presents an apparent or confirmed threat.',

    icon: <BsExclamationTriangle />,
  },

  PURSUER_MAIL: {
    label: 'Pursuer Mail',

    description:
      'Mail or package associated with a pursuer or unwanted contact.',

    icon: <BsEnvelope />,
  },
};

/* =========================================================
   DELIVERY COMPANY LABELS
   ========================================================= */

const DELIVERY_COMPANY_LABELS: Partial<Record<DeliveryCompany, string>> = {
  AMAZON: 'Amazon',

  DHL: 'DHL',

  FEDEX: 'FedEx',

  UPS: 'UPS',

  USPS: 'USPS',

  COURIER: 'Courier',

  HAND_DELIVERED: 'Hand Delivered',

  OTHER: 'Other',

  UNKNOWN: 'Unknown',
};

/* =========================================================
   HELPERS
   ========================================================= */

function enumLabel(value: string) {
  return value
    .toLowerCase()
    .split('_')
    .map((section) => section.charAt(0).toUpperCase() + section.slice(1))
    .join(' ');
}

function companyLabel(value: DeliveryCompany) {
  return DELIVERY_COMPANY_LABELS[value] ?? enumLabel(value);
}

function getErrorMessage(
  error: any,

  fallback: string
) {
  return error?.response?.data?.msg || error?.message || fallback;
}

/* =========================================================
   DATE / TIME
   ========================================================= */

function currentDateTimeParts(timezone: string) {
  const now = moment().tz(timezone || 'America/Los_Angeles');

  return {
    date: now.format('YYYY-MM-DD'),

    time: now.format('HH:mm'),
  };
}

function isoToDateTimeParts(
  value: string | undefined,

  timezone: string
) {
  if (!value) {
    return {
      date: '',

      time: '',
    };
  }

  const parsed = moment(value).tz(timezone || 'America/Los_Angeles');

  if (!parsed.isValid()) {
    return {
      date: '',

      time: '',
    };
  }

  return {
    date: parsed.format('YYYY-MM-DD'),

    time: parsed.format('HH:mm'),
  };
}

function localDateTimeToIso(
  date: string,

  time: string,

  timezone: string
) {
  if (!date || !time) {
    return undefined;
  }

  const parsed = moment.tz(
    `${date} ${time}`,

    'YYYY-MM-DD HH:mm',

    true,

    timezone || 'America/Los_Angeles'
  );

  return parsed.isValid() ? parsed.toISOString() : undefined;
}

/* =========================================================
   STRINGS
   ========================================================= */

function optionalString(value: string) {
  const normalized = value.trim();

  return normalized || undefined;
}

/* =========================================================
   EMPTY FORM
   ========================================================= */

function createEmptyForm(
  timezone: string,

  classification: DeliveryClassification = 'NORMAL'
): DeliveryForm {
  const now = currentDateTimeParts(timezone);

  return {
    classification,

    receivedDate: now.date,

    receivedTime: now.time,

    deliveryCompany: '',

    deliveryCompanyOther: '',

    recipientName: '',

    notes: '',

    notifyRecipientRequested: false,
  };
}

/* =========================================================
   DELIVERY -> FORM
   ========================================================= */

function deliveryToForm(
  delivery: Delivery,

  timezone: string
): DeliveryForm {
  const received = isoToDateTimeParts(
    delivery.receivedAt,

    timezone
  );

  return {
    classification: delivery.classification,

    receivedDate: received.date,

    receivedTime: received.time,

    deliveryCompany: delivery.deliveryCompany ?? '',

    deliveryCompanyOther: delivery.deliveryCompanyOther ?? '',

    recipientName: delivery.recipientName ?? '',

    notes: delivery.notes ?? '',

    notifyRecipientRequested: delivery.notifyRecipientRequested ?? false,
  };
}

/* =========================================================
   FORM -> PAYLOAD
   ========================================================= */

function buildPayload(
  form: DeliveryForm,

  timezone: string
): Omit<CreateDeliveryPayload, 'siteId'> {
  return {
    classification: form.classification,

    receivedAt: localDateTimeToIso(
      form.receivedDate,

      form.receivedTime,

      timezone
    ),

    deliveryCompany: form.deliveryCompany || undefined,

    deliveryCompanyOther:
      form.deliveryCompany === 'OTHER'
        ? optionalString(form.deliveryCompanyOther)
        : undefined,

    recipientName: optionalString(form.recipientName),

    notes: optionalString(form.notes),

    notifyRecipientRequested: form.notifyRecipientRequested,
  };
}

/* =========================================================
   CURRENT USER DISPLAY
   ========================================================= */

function getCurrentUserName(userState: any) {
  const user = userState?.user ?? userState ?? {};

  const firstName = user.firstName ?? user.first ?? user.given_name ?? '';

  const lastName = user.lastName ?? user.last ?? user.family_name ?? '';

  const name = [firstName, lastName].filter(Boolean).join(' ').trim();

  return name || user.name || user.email || 'Current signed-in user';
}

/* =========================================================
   FILE SIZE
   ========================================================= */

function formatFileSize(size: number | undefined) {
  if (size === undefined || !Number.isFinite(size)) {
    return '';
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/* =========================================================
   COMPONENT
   ========================================================= */

const Deliveries: React.FC = () => {
  /* =====================================================
       REDUX / SITE
       ===================================================== */

  const currentSite = useSelector((state: any) => state.currentSite ?? {});

  const currentUser = useSelector((state: any) => state.user ?? {});

  const siteId = currentSite?.siteId ?? '';

  const deliveryRealtimeVersion = useSelector((state: any) =>
    selectDeliveryRealtimeVersion(
      state,

      siteId
    )
  );

  const lastDeliveryRealtimeUpdate = useSelector((state: any) =>
    selectLastDeliveryUpdate(
      state,

      siteId
    )
  );

  const siteName = currentSite?.siteName ?? '';

  const timezone = currentSite?.timezone || 'America/Los_Angeles';

  const validSite = Boolean(
    siteId && siteId !== '0000' && siteName !== 'MASTER'
  );

  const currentUserName = useMemo(
    () => getCurrentUserName(currentUser),

    [currentUser]
  );

  /*
   * Realtime checkpoints.
   *
   * These prevent initial render and site changes from
   * being mistaken for new Delivery socket events.
   */
  const previousRealtimeVersion = useRef(deliveryRealtimeVersion);

  const previousRealtimeSiteId = useRef(siteId);

  /* =====================================================
       CONFIG
       ===================================================== */

  const [config, setConfig] = useState<DeliveryConfig | null>(null);

  const [configLoading, setConfigLoading] = useState(true);

  /* =====================================================
       FORM / RECORD
       ===================================================== */

  const [form, setForm] = useState<DeliveryForm>(() =>
    createEmptyForm(timezone)
  );

  const [delivery, setDelivery] = useState<Delivery | null>(null);

  const [deliveryId, setDeliveryId] = useState<string | null>(null);

  const [status, setStatus] = useState<DeliveryStatus | null>(null);

  const readOnly = status === 'SUBMITTED';

  /* =====================================================
       IMAGE
       ===================================================== */

  const [imageFile, setImageFile] = useState<File | null>(null);

  const [localPreviewUrl, setLocalPreviewUrl] = useState('');

  /*
   * Build and safely clean up browser object URLs.
   */

  useEffect(() => {
    if (!imageFile) {
      setLocalPreviewUrl('');

      return;
    }

    const url = URL.createObjectURL(imageFile);

    setLocalPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageFile]);

  const imagePreviewUrl = localPreviewUrl || delivery?.image?.url || '';

  const hasStoredImage = Boolean(delivery?.image?.storageKey);

  const imageRequired = Boolean(
    config?.imageRequiredFor?.includes(form.classification)
  );

  const hasImage = Boolean(imageFile || hasStoredImage);

  /* =====================================================
       FEEDBACK / LOADING
       ===================================================== */

  const [saving, setSaving] = useState(false);

  const [uploadingImage, setUploadingImage] = useState(false);

  const [error, setError] = useState('');

  const [success, setSuccess] = useState('');

  /* =====================================================
       HISTORY
       ===================================================== */

  const [showHistory, setShowHistory] = useState(false);

  const [historyLoading, setHistoryLoading] = useState(false);

  const [history, setHistory] = useState<Delivery[]>([]);

  const [historyRange, setHistoryRange] = useState<HistoryRangePreset>('1');

  const [customFrom, setCustomFrom] = useState('');

  const [customTo, setCustomTo] = useState('');

  /* =====================================================
       APPLY DELIVERY
       ===================================================== */

  const applyDelivery = useCallback(
    (next: Delivery) => {
      setDelivery(next);

      setDeliveryId(next._id);

      setStatus(next.status);

      setForm(
        deliveryToForm(
          next,

          timezone
        )
      );
    },

    [timezone]
  );

  /* =====================================================
       LOAD CONFIG
       ===================================================== */

  const loadConfig = useCallback(async () => {
    if (!validSite) {
      setConfigLoading(false);

      return;
    }

    setConfigLoading(true);

    setError('');

    try {
      const response = await getDeliveryConfig();

      if (!response.success || !response.data) {
        throw new Error(
          response.msg || 'Unable to load delivery configuration.'
        );
      }

      setConfig(response.data);

      const defaultClassification = response.data.classifications?.includes(
        'NORMAL'
      )
        ? 'NORMAL'
        : (response.data.classifications?.[0] ?? 'NORMAL');

      setForm(
        createEmptyForm(
          timezone,

          defaultClassification
        )
      );
    } catch (error: any) {
      setConfig(null);

      setError(
        getErrorMessage(
          error,

          'Unable to load delivery configuration.'
        )
      );
    } finally {
      setConfigLoading(false);
    }
  }, [timezone, validSite]);

  useEffect(() => {
    setDelivery(null);

    setDeliveryId(null);

    setStatus(null);

    setImageFile(null);

    setHistory([]);

    setShowHistory(false);

    setHistoryRange('1');

    setCustomFrom('');

    setCustomTo('');

    void loadConfig();
  }, [siteId, loadConfig]);

  /* =====================================================
       HISTORY
       ===================================================== */

  const loadHistory = useCallback(async () => {
    if (!validSite) {
      setHistory([]);

      return;
    }

    setHistoryLoading(true);

    try {
      let from: string | undefined;

      let to: string | undefined;

      if (historyRange === 'CUSTOM') {
        if (
          customFrom &&
          customTo &&
          moment(customFrom, 'YYYY-MM-DD').isAfter(
            moment(customTo, 'YYYY-MM-DD'),
            'day'
          )
        ) {
          setHistory([]);

          setError('The history start date cannot be after the end date.');

          return;
        }

        if (customFrom) {
          from = moment
            .tz(customFrom, 'YYYY-MM-DD', timezone)
            .startOf('day')
            .toISOString();
        }

        if (customTo) {
          to = moment
            .tz(customTo, 'YYYY-MM-DD', timezone)
            .endOf('day')
            .toISOString();
        }
      } else {
        const days = Number(historyRange);

        from = moment()
          .tz(timezone)
          .subtract(days - 1, 'days')
          .startOf('day')
          .toISOString();

        to = moment().tz(timezone).endOf('day').toISOString();
      }

      setError('');

      const response = await getDeliveries({
        siteId,

        from,

        to,

        limit: 250,

        skip: 0,
      });

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to load delivery history.');
      }

      setHistory(response.data.deliveries ?? []);
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to load delivery history.'
        )
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [validSite, siteId, timezone, historyRange, customFrom, customTo]);

  useEffect(() => {
    if (showHistory) {
      void loadHistory();
    }
  }, [showHistory, loadHistory]);

  /* =====================================================
       DELIVERY REALTIME REFRESH
       =====================================================

       Backend Delivery mutation
            ↓
       delivery:update
            ↓
       RealtimeSocketBridge
            ↓
       deliverySlice version increments
            ↓
       this effect
            ↓
       authoritative API refetch

       IMPORTANT:
       We do not use socket payloads as Delivery records.
       MongoDB / the authenticated HTTP API remain
       authoritative.
       ===================================================== */

  useEffect(() => {
    /*
     * Site changes already trigger the normal screen
     * lifecycle above.
     *
     * Reset the realtime checkpoint so a Delivery event
     * from the previously selected site does not trigger
     * an unnecessary fetch for the new site.
     */
    if (previousRealtimeSiteId.current !== siteId) {
      previousRealtimeSiteId.current = siteId;

      previousRealtimeVersion.current = deliveryRealtimeVersion;

      return;
    }

    /*
     * Initial render or no new socket event.
     */
    if (previousRealtimeVersion.current === deliveryRealtimeVersion) {
      return;
    }

    /*
     * Mark this event version as handled before any
     * asynchronous refetch begins.
     */
    previousRealtimeVersion.current = deliveryRealtimeVersion;

    if (!validSite) {
      return;
    }

    console.log('📦 Delivery realtime update - refreshing site:', {
      siteId,

      event: lastDeliveryRealtimeUpdate,
    });

    /*
     * If Delivery History is visible, immediately
     * refetch the authoritative list.
     */
    if (showHistory) {
      void loadHistory();
    }

    /*
     * If this exact SUBMITTED Delivery is currently
     * open, refresh its detail record as well.
     *
     * We intentionally do NOT auto-refresh an actively
     * edited DRAFT because doing so could overwrite
     * unsaved local form changes.
     */
    if (
      deliveryId &&
      status === 'SUBMITTED' &&
      lastDeliveryRealtimeUpdate?.deliveryId === deliveryId
    ) {
      void (async () => {
        try {
          const response = await getDeliveryById(
            deliveryId,

            siteId
          );

          if (response.success && response.data) {
            applyDelivery(response.data);
          }
        } catch (realtimeError) {
          console.error(
            'Delivery realtime detail refresh failed:',
            realtimeError
          );
        }
      })();
    }
  }, [
    deliveryRealtimeVersion,

    siteId,

    validSite,

    showHistory,

    loadHistory,

    lastDeliveryRealtimeUpdate,

    deliveryId,

    status,

    applyDelivery,
  ]);

  const toggleHistory = () => {
    setShowHistory((previous) => !previous);
  };

  /* =====================================================
       NEW DELIVERY
       ===================================================== */

  const newDelivery = () => {
    const defaultClassification = config?.classifications?.includes('NORMAL')
      ? 'NORMAL'
      : (config?.classifications?.[0] ?? 'NORMAL');

    setDelivery(null);

    setDeliveryId(null);

    setStatus(null);

    setImageFile(null);

    setForm(
      createEmptyForm(
        timezone,

        defaultClassification
      )
    );

    setError('');

    setSuccess('');

    window.scrollTo({
      top: 0,

      behavior: 'smooth',
    });
  };

  /* =====================================================
       IMAGE FILE VALIDATION
       ===================================================== */

  const selectImage = (file: File | null) => {
    if (readOnly || !file) {
      return;
    }

    setError('');

    const allowedTypes = new Set(['image/jpeg', 'image/png']);

    if (!allowedTypes.has(file.type)) {
      setError('Delivery image must be a JPG or PNG file.');

      return;
    }

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      setError('Delivery image cannot exceed 10 MB.');

      return;
    }

    setImageFile(file);
  };

  /* =====================================================
       BUILD / SAVE BASE DRAFT
       ===================================================== */

  const saveBaseDraft = async () => {
    const fields = buildPayload(
      form,

      timezone
    );

    if (deliveryId) {
      const response = await updateDeliveryDraft(
        deliveryId,

        siteId,

        fields
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to update delivery draft.');
      }

      return response.data;
    }

    const response = await createDelivery({
      siteId,

      ...fields,
    });

    if (!response.success || !response.data) {
      throw new Error(response.msg || 'Unable to create delivery draft.');
    }

    return response.data;
  };

  /* =====================================================
       UPLOAD SELECTED IMAGE
       ===================================================== */

  const uploadSelectedImage = async (id: string) => {
    if (!imageFile) {
      return null;
    }

    setUploadingImage(true);

    try {
      const response = await uploadDeliveryImage(
        id,

        siteId,

        imageFile
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to upload delivery image.');
      }

      setImageFile(null);

      return response.data;
    } finally {
      setUploadingImage(false);
    }
  };

  /* =====================================================
       SAVE DRAFT
       ===================================================== */

  const saveDraft = async () => {
    if (!validSite || readOnly) {
      return;
    }

    setSaving(true);

    setError('');

    setSuccess('');

    try {
      let saved = await saveBaseDraft();

      setDeliveryId(saved._id);

      setDelivery(saved);

      setStatus(saved.status);

      if (imageFile) {
        const uploaded = await uploadSelectedImage(saved._id);

        if (uploaded) {
          saved = uploaded;
        }
      }

      applyDelivery(saved);

      setSuccess('Delivery draft saved successfully.');

      if (showHistory) {
        await loadHistory();
      }
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to save delivery draft.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
       VALIDATE SUBMISSION
       ===================================================== */

  const validateForSubmit = () => {
    if (!form.receivedDate || !form.receivedTime) {
      return 'Delivery date and time are required.';
    }

    if (
      !localDateTimeToIso(
        form.receivedDate,

        form.receivedTime,

        timezone
      )
    ) {
      return 'Invalid delivery date or time.';
    }

    if (!form.deliveryCompany) {
      return 'Delivery company is required.';
    }

    if (form.deliveryCompany === 'OTHER' && !form.deliveryCompanyOther.trim()) {
      return 'Please enter the delivery company.';
    }

    if (imageRequired && !hasImage) {
      const label =
        CLASSIFICATION_META[form.classification]?.label ??
        enumLabel(form.classification);

      return `An image is required for ${label} deliveries.`;
    }

    return '';
  };

  /* =====================================================
       SUBMIT
       ===================================================== */

  const submit = async () => {
    if (readOnly || !validSite) {
      return;
    }

    const validation = validateForSubmit();

    if (validation) {
      setError(validation);

      return;
    }

    const confirmed = window.confirm(
      'Submit this delivery? Submitted delivery records cannot be edited.'
    );

    if (!confirmed) {
      return;
    }

    setSaving(true);

    setError('');

    setSuccess('');

    try {
      /*
       * 1. Create/update Mongo draft.
       */

      let saved = await saveBaseDraft();

      const id = saved._id;

      if (!id) {
        throw new Error('Delivery ID was not returned.');
      }

      setDeliveryId(id);

      setDelivery(saved);

      setStatus(saved.status);

      /*
       * 2. Upload/replace package image.
       */

      if (imageFile) {
        const uploaded = await uploadSelectedImage(id);

        if (uploaded) {
          saved = uploaded;

          setDelivery(uploaded);
        }
      }

      /*
       * 3. Server-authoritative final submission.
       */

      const response = await submitDelivery(
        id,

        siteId
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to submit delivery.');
      }

      applyDelivery(response.data);

      setSuccess('Delivery submitted successfully.');

      await loadHistory();
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to submit delivery.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
       REMOVE IMAGE
       ===================================================== */

  const removeImage = async () => {
    if (readOnly) {
      return;
    }

    /*
     * If a new local replacement is selected,
     * simply discard that selection first.
     */

    if (imageFile) {
      setImageFile(null);

      return;
    }

    if (!deliveryId || !hasStoredImage) {
      return;
    }

    const confirmed = window.confirm('Remove this delivery image?');

    if (!confirmed) {
      return;
    }

    setSaving(true);

    setError('');

    try {
      const response = await deleteDeliveryImage(
        deliveryId,

        siteId
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to remove delivery image.');
      }

      applyDelivery(response.data);
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to remove delivery image.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
       OPEN HISTORY RECORD
       ===================================================== */

  const openDelivery = async (id: string) => {
    setSaving(true);

    setError('');

    setSuccess('');

    try {
      const response = await getDeliveryById(
        id,

        siteId
      );

      if (!response.success || !response.data) {
        throw new Error(response.msg || 'Unable to open delivery.');
      }

      setImageFile(null);

      applyDelivery(response.data);

      window.scrollTo({
        top: 0,

        behavior: 'smooth',
      });
    } catch (error: any) {
      setError(
        getErrorMessage(
          error,

          'Unable to open delivery.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
       NO SITE
       ===================================================== */

  if (!validSite) {
    return (
      <div className={styles.page}>
        <div className={styles.noSiteState}>
          <BsBoxSeam />

          <h1>Select a site</h1>

          <p>Select a client site before logging or reviewing deliveries.</p>
        </div>
      </div>
    );
  }

  /* =====================================================
       CONFIG LOADING
       ===================================================== */

  if (configLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.configState}>
          <BsBoxSeam />

          <h1>Loading Deliveries</h1>

          <p>Loading delivery configuration…</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.page}>
        <div className={styles.configState}>
          <BsExclamationTriangle />

          <h1>Deliveries unavailable</h1>

          <p>{error || 'Unable to load the Delivery configuration.'}</p>

          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void loadConfig()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* =====================================================
       UI
       ===================================================== */

  return (
    <div className={styles.page}>
      {/* =================================================
            HEADER
            ================================================= */}

      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <div className={styles.headerIcon}>
            <BsBoxSeam />
          </div>

          <div className={styles.titleCopy}>
            <div className={styles.headerTitleRow}>
              <h1 className={styles.headerTitle}>Deliveries</h1>

              <span className={styles.sitePill}>{siteName}</span>

              {status && (
                <span
                  className={[
                    styles.statusPill,

                    status === 'SUBMITTED'
                      ? styles.statusSubmitted
                      : styles.statusDraft,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {status === 'SUBMITTED' ? 'Submitted' : 'Draft'}
                </span>
              )}
            </div>

            <p className={styles.headerSubtitle}>
              Log incoming deliveries and document normal, suspicious,
              threatening, or pursuer-related mail and packages.
            </p>
          </div>
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={newDelivery}
          >
            <span>+</span>
            New Delivery
          </button>

          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => void toggleHistory()}
          >
            <BsClockHistory />

            {showHistory ? 'Hide History' : 'View History'}
          </button>
        </div>
      </div>

      {/* =================================================
            FEEDBACK
            ================================================= */}

      {error && (
        <div className={styles.errorBanner}>
          <BsExclamationTriangle />

          {error}
        </div>
      )}

      {success && <div className={styles.successBanner}>{success}</div>}

      {readOnly && (
        <div className={styles.submittedNotice}>
          <BsCheckCircleFill />

          <div>
            <strong>Submitted Delivery</strong>

            <span>Submitted delivery records are read-only.</span>
          </div>
        </div>
      )}

      {/* =================================================
            WORKSPACE
            ================================================= */}

      <section className={styles.workspace}>
        {/* ===============================================
              CLASSIFICATION
              =============================================== */}

        <div className={styles.classificationPanel}>
          <div className={styles.panelHeading}>
            <div className={styles.stepNumber}>1</div>

            <div>
              <h2>Package Classification</h2>

              <p>Select the category that best matches the delivery.</p>
            </div>
          </div>

          <div className={styles.classificationGrid}>
            {config.classifications.map((classification) => {
              const selected = form.classification === classification;

              const meta = CLASSIFICATION_META[classification];

              return (
                <button
                  key={classification}
                  type="button"
                  disabled={readOnly}
                  className={[
                    styles.classificationCard,

                    selected ? styles.classificationCardSelected : '',

                    selected && classification === 'NORMAL'
                      ? styles.selectedNormal
                      : '',

                    selected && classification === 'SUSPICIOUS'
                      ? styles.selectedSuspicious
                      : '',

                    selected && classification === 'THREAT'
                      ? styles.selectedThreat
                      : '',

                    selected && classification === 'PURSUER_MAIL'
                      ? styles.selectedPursuer
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    if (readOnly) {
                      return;
                    }

                    setForm((previous) => ({
                      ...previous,

                      classification,
                    }));

                    setError('');
                  }}
                >
                  {selected && <span className={styles.selectionCheck}>✓</span>}

                  <span className={styles.classificationIcon}>
                    {meta?.icon}
                  </span>

                  <span className={styles.classificationLabel}>
                    {meta?.label ?? enumLabel(classification)}
                  </span>

                  <span className={styles.classificationDescription}>
                    {meta?.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===============================================
              DETAILS
              =============================================== */}

        <div className={styles.detailsPanel}>
          <div className={styles.panelHeading}>
            <div className={styles.stepNumber}>2</div>

            <div>
              <h2>Delivery Details</h2>

              <div
                className={[
                  styles.classificationBadge,

                  styles[`classification${form.classification}`],
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                Classification:{' '}
                {CLASSIFICATION_META[form.classification]?.label ??
                  enumLabel(form.classification)}
              </div>
            </div>
          </div>

          <div className={styles.detailsGrid}>
            {/* DATE */}

            <label className={styles.field}>
              <span>
                Date
                <b>*</b>
              </span>

              <input
                type="date"
                value={form.receivedDate}
                disabled={readOnly}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    receivedDate: event.target.value,
                  }))
                }
              />
            </label>

            {/* TIME */}

            <label className={styles.field}>
              <span>
                Time
                <b>*</b>
              </span>

              <input
                type="time"
                value={form.receivedTime}
                disabled={readOnly}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    receivedTime: event.target.value,
                  }))
                }
              />
            </label>

            {/* DELIVERY COMPANY */}

            <label
              className={[styles.field, styles.fullWidth]
                .filter(Boolean)
                .join(' ')}
            >
              <span>
                Delivery Company
                <b>*</b>
              </span>

              <select
                value={form.deliveryCompany}
                disabled={readOnly}
                onChange={(event) => {
                  const value = event.target.value as DeliveryCompany | '';

                  setForm((previous) => ({
                    ...previous,

                    deliveryCompany: value,

                    deliveryCompanyOther:
                      value === 'OTHER' ? previous.deliveryCompanyOther : '',
                  }));
                }}
              >
                <option value="">Select delivery company</option>

                {config.deliveryCompanies.map((company) => (
                  <option key={company} value={company}>
                    {companyLabel(company)}
                  </option>
                ))}
              </select>
            </label>

            {/* OTHER COMPANY */}

            {form.deliveryCompany === 'OTHER' && (
              <label
                className={[styles.field, styles.fullWidth]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span>
                  Company Name
                  <b>*</b>
                </span>

                <input
                  type="text"
                  placeholder="Enter delivery company"
                  value={form.deliveryCompanyOther}
                  disabled={readOnly}
                  maxLength={250}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,

                      deliveryCompanyOther: event.target.value,
                    }))
                  }
                />
              </label>
            )}

            {/* RECIPIENT */}

            <div
              className={[styles.field, styles.fullWidth]
                .filter(Boolean)
                .join(' ')}
            >
              <span>Recipient</span>

              <div className={styles.recipientRow}>
                <input
                  type="text"
                  placeholder="Intended recipient"
                  value={form.recipientName}
                  disabled={readOnly}
                  maxLength={500}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,

                      recipientName: event.target.value,
                    }))
                  }
                />

                <button
                  type="button"
                  disabled={readOnly || !form.recipientName.trim()}
                  className={[
                    styles.notifyButton,

                    form.notifyRecipientRequested
                      ? styles.notifyButtonActive
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() =>
                    setForm((previous) => ({
                      ...previous,

                      notifyRecipientRequested:
                        !previous.notifyRecipientRequested,
                    }))
                  }
                >
                  <BsBell />

                  {form.notifyRecipientRequested
                    ? 'Notification Requested'
                    : 'Notify Recipient'}
                </button>
              </div>

              <small className={styles.fieldHint}>
                Recipient notification is recorded only. The notification
                delivery process will be connected later.
              </small>
            </div>

            {/* RECEIVING PERSON */}

            <label
              className={[styles.field, styles.fullWidth]
                .filter(Boolean)
                .join(' ')}
            >
              <span>
                Receiving Person
                <b>*</b>
              </span>

              <div className={styles.readOnlyInputWrap}>
                <BsPersonFill />

                <input
                  type="text"
                  readOnly
                  value={delivery?.receivedByNameSnapshot || currentUserName}
                />
              </div>

              <small className={styles.fieldHint}>
                The backend records the authenticated EdLogger user.
              </small>
            </label>
          </div>

          {/* =============================================
                IMAGE
                ============================================= */}

          <div className={styles.imageSection}>
            <div className={styles.imageHeader}>
              <div>
                <strong>
                  Package Image
                  {imageRequired && <b> *</b>}
                </strong>

                <span>JPG or PNG • Maximum 10 MB</span>
              </div>
            </div>

            {imagePreviewUrl ? (
              <div className={styles.imagePreviewCard}>
                <img
                  src={imagePreviewUrl}
                  alt="Delivery package"
                  className={styles.imagePreview}
                />

                <div className={styles.imageMeta}>
                  <div>
                    <strong>
                      {imageFile?.name ||
                        delivery?.image?.fileName ||
                        'Delivery image'}
                    </strong>

                    <span>
                      {imageFile
                        ? formatFileSize(imageFile.size)
                        : formatFileSize(delivery?.image?.size)}
                    </span>
                  </div>

                  {!readOnly && (
                    <div className={styles.imageActions}>
                      <label className={styles.replaceImageButton}>
                        Replace
                        <input
                          type="file"
                          accept="image/jpeg,image/png"
                          className={styles.hiddenFileInput}
                          disabled={saving}
                          onChange={(event) => {
                            selectImage(event.target.files?.[0] ?? null);

                            event.currentTarget.value = '';
                          }}
                        />
                      </label>

                      <button
                        type="button"
                        className={styles.removeImageButton}
                        disabled={saving}
                        onClick={() => void removeImage()}
                      >
                        <BsTrash />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <label
                className={[
                  styles.uploadZone,

                  imageRequired ? styles.uploadZoneRequired : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragOver={(event) => {
                  event.preventDefault();
                }}
                onDrop={(event) => {
                  event.preventDefault();

                  if (readOnly) {
                    return;
                  }

                  selectImage(event.dataTransfer.files?.[0] ?? null);
                }}
              >
                <BsUpload className={styles.uploadIcon} />

                <strong>Upload Image</strong>

                <span>Drag and drop an image here, or click to browse</span>

                <small>JPG or PNG • Maximum 10 MB</small>

                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  className={styles.hiddenFileInput}
                  disabled={readOnly}
                  onChange={(event) => {
                    selectImage(event.target.files?.[0] ?? null);

                    event.currentTarget.value = '';
                  }}
                />
              </label>
            )}

            {imageRequired && (
              <div className={styles.imageRequirement}>
                <BsExclamationTriangle />

                <span>
                  An image is required for Threat, Suspicious, and Pursuer Mail
                  deliveries.
                </span>
              </div>
            )}
          </div>

          {/* =============================================
                NOTES
                ============================================= */}

          <div className={styles.notesSection}>
            <label className={styles.field}>
              <span>Notes</span>

              <textarea
                rows={6}
                maxLength={10000}
                disabled={readOnly}
                placeholder="Add relevant details about the delivery..."
                value={form.notes}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,

                    notes: event.target.value,
                  }))
                }
              />
            </label>
          </div>

          {/* =============================================
                ACTIONS
                ============================================= */}

          <div className={styles.actionBar}>
            {!readOnly && (
              <>
                <button
                  type="button"
                  className={styles.saveButton}
                  disabled={saving || uploadingImage}
                  onClick={() => void saveDraft()}
                >
                  <BsBookmark />

                  {saving
                    ? 'Saving…'
                    : deliveryId
                      ? 'Update Draft'
                      : 'Save Draft'}
                </button>

                <button
                  type="button"
                  className={styles.submitButton}
                  disabled={saving || uploadingImage}
                  onClick={() => void submit()}
                >
                  <BsSendFill />

                  {saving ? 'Processing…' : 'Submit Delivery'}
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* =================================================
            HISTORY
            ================================================= */}

      {showHistory && (
        <section className={styles.historyCard}>
          <div className={styles.historyHeader}>
            <div>
              <h2>Delivery History</h2>

              <p>{siteName}</p>
            </div>

            <div className={styles.historyCount}>
              {history.length}{' '}
              {history.length === 1 ? 'delivery' : 'deliveries'}
            </div>
          </div>

          <div className={styles.historyFilters}>
            <div className={styles.historyPresetButtons}>
              {[
                ['1', 'Same Day'],
                ['7', '7 Days'],
                ['30', '30 Days'],
                ['90', '90 Days'],
                ['180', '180 Days'],
                ['365', '365 Days'],
                ['CUSTOM', 'Date Range'],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={[
                    styles.historyFilterButton,

                    historyRange === value
                      ? styles.historyFilterButtonActive
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    const nextRange = value as HistoryRangePreset;

                    if (nextRange === 'CUSTOM' && !customFrom && !customTo) {
                      setCustomFrom(
                        moment()
                          .tz(timezone)
                          .subtract(29, 'days')
                          .format('YYYY-MM-DD')
                      );

                      setCustomTo(moment().tz(timezone).format('YYYY-MM-DD'));
                    }

                    setHistoryRange(nextRange);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {historyRange === 'CUSTOM' && (
              <div className={styles.customDateRange}>
                <label>
                  <span>From</span>

                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || undefined}
                    onChange={(event) => setCustomFrom(event.target.value)}
                  />
                </label>

                <label>
                  <span>To</span>

                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    onChange={(event) => setCustomTo(event.target.value)}
                  />
                </label>
              </div>
            )}
          </div>

          {historyLoading ? (
            <div className={styles.historyEmpty}>Loading delivery history…</div>
          ) : history.length === 0 ? (
            <div className={styles.historyEmpty}>
              No delivery records found for this site.
            </div>
          ) : (
            <div className={styles.historyList}>
              {history.map((item) => {
                const received = item.receivedAt
                  ? moment(item.receivedAt).tz(timezone)
                  : null;

                const company =
                  item.deliveryCompany === 'OTHER'
                    ? item.deliveryCompanyOther || 'Other'
                    : item.deliveryCompany
                      ? companyLabel(item.deliveryCompany)
                      : 'Unknown';

                return (
                  <button
                    key={item._id}
                    type="button"
                    className={styles.historyRow}
                    onClick={() => void openDelivery(item._id)}
                  >
                    <div className={styles.historyDate}>
                      <strong>{received?.format('MMM D') ?? 'No Date'}</strong>

                      <span>{received?.format('h:mm A') ?? ''}</span>
                    </div>

                    <div className={styles.historyMain}>
                      <strong>{company}</strong>

                      <span>
                        {item.recipientName
                          ? `For ${item.recipientName}`
                          : 'Recipient not recorded'}
                      </span>

                      {item.receivedByNameSnapshot && (
                        <small>Received by {item.receivedByNameSnapshot}</small>
                      )}
                    </div>

                    <div className={styles.historyBadges}>
                      <span
                        className={[
                          styles.classificationBadge,

                          styles[`classification${item.classification}`],
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {CLASSIFICATION_META[item.classification]?.label ??
                          enumLabel(item.classification)}
                      </span>

                      <span
                        className={[
                          styles.historyStatus,

                          item.status === 'SUBMITTED'
                            ? styles.historySubmitted
                            : styles.historyDraft,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {item.status === 'SUBMITTED' ? 'Submitted' : 'Draft'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Deliveries;
